/**
 * exportSeancePdf.js — Génération PDF d'une Fiche de Séance Live.
 *
 * Utilisation :
 *   import { exportSeancePdf } from '@/utils/exportSeancePdf';
 *   exportSeancePdf({ code, livret, concis, phraseClef, state, stats, accent });
 *
 * Approche : 100% client (jsPDF). Pas de capture canvas → PDF léger, sélectionnable,
 * imprimable, paginé automatiquement.
 */
import { jsPDF } from 'jspdf';

/* ── Helpers couleur ─────────────────────────────────────────────── */
function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') return { r: 8, g: 145, b: 178 };
  const h = hex.replace('#', '');
  if (h.length !== 6) return { r: 8, g: 145, b: 178 };
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

/* ── Helpers date / nom fichier ──────────────────────────────────── */
function formatDateFr(isoDate) {
  try {
    const d = isoDate ? new Date(isoDate) : new Date();
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return new Date().toLocaleDateString('fr-FR');
  }
}

function slugifyCode(code) {
  return (code || 'seance').replace(/[^a-zA-Z0-9-]/g, '-').toLowerCase();
}

/* ── Génération PDF principale ───────────────────────────────────── */
export function exportSeancePdf({
  code,
  livret,
  concis,
  phraseClef,
  state,
  stats,
  accent = '#0891b2',
}) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginX = 16;
  const contentW = pageW - marginX * 2;
  const { r, g, b } = hexToRgb(accent);

  let y = 0;

  /* ── Helper rendu : gestion saut de page ─────────────────────── */
  const ensureSpace = (needed) => {
    if (y + needed > pageH - 18) {
      addFooter();
      doc.addPage();
      y = 16;
    }
  };

  const addFooter = () => {
    const info = doc.internal.getCurrentPageInfo();
    const pageNum = info?.pageNumber ?? info?.pageNum ?? doc.internal.getNumberOfPages();
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(
      `Code 2 Roues · Fiche de Séance Live · ${formatDateFr(new Date().toISOString())}`,
      marginX,
      pageH - 8,
    );
    doc.text(`Page ${pageNum}`, pageW - marginX, pageH - 8, { align: 'right' });
  };

  const addSectionTitle = (label) => {
    ensureSpace(12);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(r, g, b);
    doc.text(label.toUpperCase(), marginX, y);
    // Trait sous le titre
    doc.setDrawColor(r, g, b);
    doc.setLineWidth(0.4);
    doc.line(marginX, y + 1.5, marginX + 30, y + 1.5);
    y += 7;
  };

  const addParagraph = (text, opts = {}) => {
    const {
      size = 10,
      style = 'normal',
      color = [40, 40, 40],
      italic = false,
      maxWidth = contentW,
      indent = 0,
    } = opts;
    if (!text) return;
    doc.setFont('helvetica', italic ? 'italic' : style);
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);
    const lines = doc.splitTextToSize(String(text), maxWidth - indent);
    lines.forEach((line) => {
      ensureSpace(size * 0.45 + 2);
      doc.text(line, marginX + indent, y);
      y += size * 0.45 + 1.5;
    });
  };

  const addBulletList = (items, { iconShape = 'dot', iconColor = [r, g, b] } = {}) => {
    if (!items || items.length === 0) return;
    items.forEach((it) => {
      const text = String(it);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const lines = doc.splitTextToSize(text, contentW - 8);
      ensureSpace(lines.length * 4.6 + 1);
      // Icône graphique (cercle plein, croix, ou point) → garantit compatibilité encodage
      doc.setFillColor(iconColor[0], iconColor[1], iconColor[2]);
      doc.setDrawColor(iconColor[0], iconColor[1], iconColor[2]);
      const iconY = y - 1.4;
      if (iconShape === 'check') {
        // Pastille pleine
        doc.circle(marginX + 2, iconY, 1.4, 'F');
        // Cocher blanc
        doc.setDrawColor(255, 255, 255);
        doc.setLineWidth(0.5);
        doc.line(marginX + 1.3, iconY + 0.1, marginX + 1.85, iconY + 0.7);
        doc.line(marginX + 1.85, iconY + 0.7, marginX + 2.75, iconY - 0.55);
      } else if (iconShape === 'cross') {
        doc.circle(marginX + 2, iconY, 1.4, 'F');
        doc.setDrawColor(255, 255, 255);
        doc.setLineWidth(0.5);
        doc.line(marginX + 1.3, iconY - 0.6, marginX + 2.7, iconY + 0.6);
        doc.line(marginX + 2.7, iconY - 0.6, marginX + 1.3, iconY + 0.6);
      } else {
        // Point simple
        doc.circle(marginX + 2, iconY, 0.9, 'F');
      }
      // Texte
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 40, 40);
      lines.forEach((line, i) => {
        if (i > 0) ensureSpace(4.6);
        doc.text(line, marginX + 7, y);
        y += 4.6;
      });
      y += 0.8;
    });
  };

  /* ── HEADER (bandeau couleur catégorie) ──────────────────────── */
  doc.setFillColor(r, g, b);
  doc.rect(0, 0, pageW, 26, 'F');

  // Mini-pastille REMC
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(marginX, 7, 32, 6, 1.5, 1.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(r, g, b);
  doc.text(`REMC · ${code || '—'}`, marginX + 16, 11, { align: 'center' });

  // Titre principal
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(255, 255, 255);
  doc.text('Fiche de Séance Live', marginX, 21);

  // Date à droite
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text(
    formatDateFr(state?.updatedAt || state?.startedAt || new Date().toISOString()),
    pageW - marginX,
    21,
    { align: 'right' },
  );

  y = 34;

  /* ── Intitulé livret ─────────────────────────────────────────── */
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(25, 25, 25);
  const intituleLines = doc.splitTextToSize(livret?.intitule || `Compétence ${code}`, contentW);
  intituleLines.forEach((line) => {
    ensureSpace(6);
    doc.text(line, marginX, y);
    y += 6;
  });
  y += 2;

  /* ── Objectif ────────────────────────────────────────────────── */
  const objectifShort = concis?.objectifCourt || livret?.objectif;
  if (objectifShort) {
    addSectionTitle('Objectif de la séance');
    addParagraph(`« ${objectifShort} »`, { size: 11, style: 'bold', italic: true });
    y += 2;
  }

  /* ── Phrase clé ──────────────────────────────────────────────── */
  if (phraseClef) {
    addSectionTitle('Phrase à lui dire');
    // Filet vertical accent
    const yStart = y - 2;
    addParagraph(`« ${phraseClef} »`, { size: 10, italic: true, indent: 4 });
    doc.setDrawColor(r, g, b);
    doc.setLineWidth(0.8);
    doc.line(marginX, yStart, marginX, y - 2);
    y += 2;
  }

  /* ── Bloc score / passages ───────────────────────────────────── */
  ensureSpace(22);
  const blocY = y;
  const blocH = 18;
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(r, g, b);
  doc.setLineWidth(0.4);
  doc.roundedRect(marginX, blocY, contentW, blocH, 2, 2, 'FD');

  // Score
  const colW = contentW / 3;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(110, 110, 110);
  doc.text('SCORE', marginX + 4, blocY + 5);
  doc.setFontSize(16);
  doc.setTextColor(r, g, b);
  doc.text(stats?.okRatio != null ? `${stats.okRatio}%` : '—', marginX + 4, blocY + 13);

  // Critères
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(110, 110, 110);
  doc.text('CRITÈRES OK', marginX + colW + 4, blocY + 5);
  doc.setFontSize(16);
  doc.setTextColor(25, 25, 25);
  doc.text(`${stats?.okCount || 0} / ${stats?.totalTested || 0}`, marginX + colW + 4, blocY + 13);

  // Passages
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(110, 110, 110);
  doc.text('PASSAGES', marginX + colW * 2 + 4, blocY + 5);
  doc.setFontSize(16);
  doc.setTextColor(25, 25, 25);
  doc.text(`${state?.passages || 0}`, marginX + colW * 2 + 4, blocY + 13);

  y = blocY + blocH + 6;

  /* ── Critères : Acquis ───────────────────────────────────────── */
  const criteres = concis?.criteresPrecis || [];
  const okList = criteres
    .map((c, i) => ({ c, i }))
    .filter(({ i }) => state?.critereStatuts?.[i] === 'ok')
    .map(({ c }) => c);

  if (okList.length > 0) {
    addSectionTitle(`Acquis (${okList.length})`);
    addBulletList(okList, { iconShape: 'check', iconColor: [22, 163, 74] });
    y += 2;
  }

  /* ── Critères : À retravailler ───────────────────────────────── */
  const koList = criteres
    .map((c, i) => ({ c, i }))
    .filter(({ i }) => state?.critereStatuts?.[i] === 'ko')
    .map(({ c }) => c);

  if (koList.length > 0) {
    addSectionTitle(`À retravailler (${koList.length})`);
    addBulletList(koList, { iconShape: 'cross', iconColor: [220, 38, 38] });
    y += 2;
  }

  /* ── Critères : Non testés ───────────────────────────────────── */
  const naList = criteres
    .map((c, i) => ({ c, i }))
    .filter(({ i }) => !state?.critereStatuts?.[i])
    .map(({ c }) => c);

  if (naList.length > 0 && criteres.length > 0) {
    addSectionTitle(`Non testés (${naList.length})`);
    addBulletList(naList, { iconShape: 'dot', iconColor: [140, 140, 140] });
    y += 2;
  }

  /* ── Notes briefing ──────────────────────────────────────────── */
  if (state?.briefingNotes && state.briefingNotes.trim()) {
    addSectionTitle('Notes briefing');
    addParagraph(state.briefingNotes.trim(), { size: 10 });
    y += 2;
  }

  /* ── Notes guidage ───────────────────────────────────────────── */
  if (state?.guidageNotes && state.guidageNotes.trim()) {
    addSectionTitle('Notes guidage');
    addParagraph(state.guidageNotes.trim(), { size: 10 });
    y += 2;
  }

  /* ── Libellé officiel REMC ──────────────────────────────────── */
  if (livret?.objectif && livret.objectif !== objectifShort) {
    addSectionTitle('Libellé officiel REMC');
    addParagraph(livret.objectif, { size: 9, italic: true, color: [100, 100, 100] });
  }

  /* ── Footer dernière page ────────────────────────────────────── */
  addFooter();

  /* ── Export ──────────────────────────────────────────────────── */
  const dateTag = new Date().toISOString().slice(0, 10);
  const filename = `seance-live-${slugifyCode(code)}-${dateTag}.pdf`;
  doc.save(filename);

  return filename;
}

export default exportSeancePdf;
